import { FC, ReactNode, useState, useCallback, useEffect, MouseEventHandler } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import slugify from 'dibs-slugify/exports/slugify';
import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    interactionTypeConstants,
} from 'dibs-tracking';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { Link } from 'dibs-elements/exports/Link';
import { TabList } from 'dibs-elements/exports/TabList';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { useTab } from 'dibs-elements/exports/useTab';
import { PdpRespStorytellingTab } from '../PdpRespStorytellingModule/PdpRespStorytellingTab';
import {
    PdpSharedStorytellingTrackingContextProvider,
    PdpSharedStorytellingTrackingWrapper,
} from '../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking';
import { PdpRespDealerDetails } from '../PdpRespDealerDetails/PdpRespDealerDetails';
import { PdpRespShippingReturns } from '../PdpRespDetails/PdpRespShippingReturns';
import { PdpRespTabsCustomizationPanel } from './PdpRespTabsCustomizationPanel';
import { getSelectedSku } from '../helpers/getSelectedSku';
import {
    ITEM_DETAILS,
    ABOUT_SELLER,
    CUSTOMIZATION,
    SHIPPING_RETURNS,
    labelMap,
} from '../helpers/respTabsConstants';
import { RelayVariables, Store } from '../../global/relayVariableTypes';
import { getAttributeInfo } from '../helpers/getAttributeInfo';

import { PdpRespAboutItemPanel } from '../PdpRespAboutTabs/PdpRespAboutItemPanel';

import { PdpRespDetailTabs_viewer$key } from './__generated__/PdpRespDetailTabs_viewer.graphql';
import { PdpRespDetailTabs_user$key } from './__generated__/PdpRespDetailTabs_user.graphql';
import { PdpRespDetailTabs_item$key } from './__generated__/PdpRespDetailTabs_item.graphql';

import styles from './PdpRespDetailTabs.scss';

const CustomTab: FC<{
    name: string;
    type: string;
    dataTn?: string;
    children: ReactNode;
}> = ({ name, type, dataTn, children }) => {
    const { tabCallbackRef, handleClick, isActive } = useTab(name);

    const getTrackingObject = useCallback(
        (action: string) => {
            // Shouldn't ever be used, but adding fallback to identify if unexpected tab type is ever encountered that doesn't exist in label map
            const label = labelMap[type] || `unexpected ${type}`;

            return {
                category: 'product',
                action,
                label,
                noninteraction: 0,
                eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                step_interaction_name: stepInteractionConstants.STEP_INTERACTION_PDP_TAB_CLICKED,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_PDP,
            };
        },
        [type]
    );

    // Track tab impression on mount
    useEffect(() => {
        trackEvent(getTrackingObject('pdp tab display'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        event => {
            handleClick();
            trackEvent(getTrackingObject('pdp tab clicked'), event);
        },
        [handleClick, getTrackingObject]
    );

    return (
        <li className={styles.navTabItem}>
            <Link
                buyerLinkType="linkNoDecoration"
                linkRef={tabCallbackRef}
                className={classnames(styles.navTabLink, { [styles.active]: isActive })}
                onClick={onClick}
                dataTn={dataTn}
            >
                {children}
            </Link>
        </li>
    );
};

type Props = {
    showHeader?: boolean;
    viewer: PdpRespDetailTabs_viewer$key;
    user: PdpRespDetailTabs_user$key;
    item: PdpRespDetailTabs_item$key;
    currency: string;
    selectedSkuId: string | null;
    showExpandingArea?: boolean;
    dataTn?: string;
    itemQuantity: number | null;
};

const viewerFragment = graphql`
    fragment PdpRespDetailTabs_viewer on Viewer {
        ...PdpRespDealerDetails_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespDetailTabs_user on User {
        ...PdpRespAboutItemPanel_user
        ...PdpRespDealerDetails_user
        ...PdpRespShippingReturns_user
        ...PdpRespTabsCustomizationPanel_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespDetailTabs_item on Item {
        skus {
            serviceId
            ...PdpRespDealerDetails_sku @include(if: $isMultiSku)
        }
        storyTelling: storyTellingModule(shortTitles: true) {
            tabs {
                tabType
                tabTitle
                ...PdpRespStorytellingTab_storyTellingTab
            }
        }
        seller {
            isAccessible
        }
        isCustomizable
        isDyoCustomizable
        attributes {
            customizable {
                option {
                    serviceId
                }
                text
            }
            ringResizable {
                option {
                    serviceId
                }
            }
        }
        isListingMigrated
        ...PdpRespAboutItemPanel_item
        ...PdpRespDealerDetails_item
        ...PdpRespShippingReturns_item
        ...PdpRespTabsCustomizationPanel_item
    }
`;

export const PdpRespDetailTabs: FC<Props> = ({
    selectedSkuId,
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    itemQuantity,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const tabs = item?.storyTelling?.tabs || [];
    const [activeTab, setActiveTab] = useState(ITEM_DETAILS);
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );
    const { userId, itemId, isClient, isMultiSku } = relayVariables;
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const isSellerAccessible = item?.seller?.isAccessible;
    const isListingMigrated = item?.isListingMigrated;

    const { isCustomizable = null, isDyoCustomizable, attributes } = item || {};
    const { customizationOptions = null, isResizable = false } = getAttributeInfo(attributes);

    const showCustomizationOptions =
        (isCustomizable && !!customizationOptions) || isDyoCustomizable || isResizable;

    return (
        <div className={styles.container}>
            <CurrencyProvider
                render={({ currency }: { currency: string }) => {
                    return (
                        <div className={styles.innerContainer}>
                            <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
                                <div className={styles.tabListWrapper}>
                                    <TabList fullWidth customTab indicatorPadding="xSmall">
                                        {[
                                            [
                                                ITEM_DETAILS,
                                                <CustomTab
                                                    key={ITEM_DETAILS}
                                                    name={ITEM_DETAILS}
                                                    type={ITEM_DETAILS}
                                                    dataTn="item-details-tab"
                                                >
                                                    <FormattedMessage
                                                        id="PdpRespDetailTabs.itemDetails"
                                                        defaultMessage="Item Details"
                                                    />
                                                </CustomTab>,
                                            ],
                                            ...tabs
                                                .filter(filterNulls)
                                                .map<[string, ReactNode]>((tab, index) => {
                                                    const tabKey = `${slugify(
                                                        tab.tabTitle
                                                    )}-${index}`;
                                                    return [
                                                        tabKey,
                                                        <CustomTab
                                                            key={tabKey}
                                                            name={tabKey}
                                                            type={tab.tabType}
                                                            dataTn={`${tabKey}-tab`}
                                                        >
                                                            {tab.tabTitle}
                                                        </CustomTab>,
                                                    ];
                                                }),
                                            showCustomizationOptions && [
                                                CUSTOMIZATION,
                                                <CustomTab
                                                    key={CUSTOMIZATION}
                                                    name={CUSTOMIZATION}
                                                    type={CUSTOMIZATION}
                                                    dataTn="customization-tab"
                                                >
                                                    <FormattedMessage
                                                        id="PdpRespDetailTabs.customization"
                                                        defaultMessage="Customization"
                                                    />
                                                </CustomTab>,
                                            ],
                                            isSellerAccessible && [
                                                ABOUT_SELLER,
                                                <CustomTab
                                                    key={ABOUT_SELLER}
                                                    name={ABOUT_SELLER}
                                                    type={ABOUT_SELLER}
                                                    dataTn="about-the-seller-tab"
                                                >
                                                    <FormattedMessage
                                                        id="PdpRespDetailTabs.aboutSeller"
                                                        defaultMessage="Seller Information"
                                                    />
                                                </CustomTab>,
                                            ],
                                            isSellerAccessible &&
                                                !isListingMigrated && [
                                                    SHIPPING_RETURNS,
                                                    <CustomTab
                                                        key={SHIPPING_RETURNS}
                                                        name={SHIPPING_RETURNS}
                                                        type={SHIPPING_RETURNS}
                                                        dataTn="shipping-and-returns-tab"
                                                    >
                                                        <FormattedMessage
                                                            id="PdpRespDetailTabs.shippingAndReturns"
                                                            defaultMessage="Shipping & Returns"
                                                        />
                                                    </CustomTab>,
                                                ],
                                        ]}
                                    </TabList>
                                </div>

                                <div className={styles.panelsWrapper}>
                                    <PdpSharedStorytellingTrackingContextProvider>
                                        <TabPanels renderOnInactive>
                                            {[
                                                [
                                                    ITEM_DETAILS,
                                                    <PdpRespAboutItemPanel
                                                        user={user}
                                                        item={item}
                                                        currency={currency}
                                                        selectedSkuId={selectedSkuId}
                                                        showHeader={false}
                                                        showExpandingArea={
                                                            activeTab === ITEM_DETAILS
                                                        }
                                                    />,
                                                ],
                                                ...tabs
                                                    .filter(filterNulls)
                                                    .map<[string, ReactNode]>((tab, index) => {
                                                        const tabKey = `${slugify(
                                                            tab.tabTitle
                                                        )}-${index}`;
                                                        return [
                                                            tabKey,
                                                            <PdpSharedStorytellingTrackingWrapper
                                                                key={tabKey}
                                                            >
                                                                <PdpRespStorytellingTab
                                                                    showHeader={false}
                                                                    wrapperClass={
                                                                        styles.storyTellingWrapper
                                                                    }
                                                                    storyTellingTab={tab}
                                                                    clearBackground={false}
                                                                    //Because of the current inactive tab rendering we need this workaraound to make expanding area functional.
                                                                    showExpandingArea={
                                                                        activeTab === tabKey
                                                                    }
                                                                    dataTn={`${tabKey}-tab-content`}
                                                                />
                                                            </PdpSharedStorytellingTrackingWrapper>,
                                                        ];
                                                    }),
                                                showCustomizationOptions && [
                                                    CUSTOMIZATION,
                                                    <PdpRespTabsCustomizationPanel
                                                        item={item}
                                                        user={user}
                                                    />,
                                                ],
                                                isSellerAccessible && [
                                                    ABOUT_SELLER,
                                                    <PdpRespDealerDetails
                                                        user={user}
                                                        item={item}
                                                        sku={
                                                            isMultiSku && selectedSku
                                                                ? selectedSku
                                                                : null
                                                        }
                                                        viewer={viewer}
                                                        currency={currency}
                                                        isClient={isClient}
                                                        dataTn="about-the-seller-tab-content"
                                                        hideBorder
                                                    />,
                                                ],
                                                isSellerAccessible &&
                                                    !isListingMigrated && [
                                                        SHIPPING_RETURNS,
                                                        <PdpRespShippingReturns
                                                            currency={currency}
                                                            relayVariables={{
                                                                userId,
                                                                itemId,
                                                                isClient,
                                                            }}
                                                            user={user}
                                                            item={item}
                                                            itemQuantity={itemQuantity}
                                                            isCondensedTab
                                                        />,
                                                    ],
                                            ]}
                                        </TabPanels>
                                    </PdpSharedStorytellingTrackingContextProvider>
                                </div>
                            </TabProvider>
                        </div>
                    );
                }}
            />
        </div>
    );
};
